import { getFloatNumber } from 'utils/numberUtils';
import { CertificateScore, ReviewerCommentState, ScoreState, SubmitScoreData } from './type';

const convertYesNoResult = (value: string | null) => {
  return value === 'Có' ? true : value === 'Không' ? false : null;
};

const revertYesNoResult = (value: boolean | null) => {
  return value === true ? 'Có' : value === false ? 'Không' : null;
};

export const extractScore = (certificateScore: CertificateScore): ScoreState => {
  return {
    a1_score: certificateScore.a1_score,
    a2_score: certificateScore.a2_score,
    b1_score: certificateScore.b1_score,
    b2_score: certificateScore.b2_score,
    b3_score: certificateScore.b3_score,
    b4_score: certificateScore.b4_score,
    b5_score: certificateScore.b5_score,
    c1_score: certificateScore.c1_score,
    c2_score: certificateScore.c2_score,
    d1_score: certificateScore.d1_score,
    d2_score: certificateScore.d2_score,
    hinhthuc1_score: certificateScore.hinhthuc1_score,
    hinhthuc2_score: certificateScore.hinhthuc2_score
  };
};

export const extractReviewerComment = (
  certificateScore: CertificateScore
): ReviewerCommentState => {
  return {
    c_hieuqua: certificateScore.c_hieuqua,
    c_nhanrong: certificateScore.c_nhanrong,
    ca1: convertYesNoResult(certificateScore.ca1),
    ca2: convertYesNoResult(certificateScore.ca2),
    ca3: convertYesNoResult(certificateScore.ca3),
    ca_du_dk: convertYesNoResult(certificateScore.ca_du_dk),
    cb1: convertYesNoResult(certificateScore.cb1),
    cb2: convertYesNoResult(certificateScore.cb2),
    cb3: convertYesNoResult(certificateScore.cb3),
    cb_du_dk: convertYesNoResult(certificateScore.cb_du_dk)
  };
};

export const extractScoreInitiativeData = (
  detail: any,
  user: any
): { scores: ScoreState; reviewerComment: ReviewerCommentState } => {
  const certificateScore = getCertificateScore(detail, user);

  const scores = extractScore(certificateScore);
  const reviewerComment = extractReviewerComment(certificateScore);

  return {
    scores,
    reviewerComment
  };
};

export const extractScoreInitiativeDataViewFile = (
  detail: any
): { scores: ScoreState; reviewerComment: ReviewerCommentState } => {
  const certificateScore = detail;

  const scores = extractScore(certificateScore);
  const reviewerComment = extractReviewerComment(certificateScore);

  return {
    scores,
    reviewerComment
  };
};

export const getTotalScore = (scores: any[]) => {
  return scores
    .map(getFloatNumber)
    .filter(Boolean)
    .reduce((a, b) => a + b, 0);
};

export const prepareCertificateScore = (values: any): CertificateScore => {
  return {
    a1_score: values?.a1_score ?? null,
    a2_score: values?.a2_score ?? null,
    b1_score: values?.b1_score ?? null,
    b2_score: values?.b2_score ?? null,
    b3_score: values?.b3_score ?? null,
    b4_score: values?.b4_score ?? null,
    b5_score: values?.b5_score ?? null,
    c1_score: values?.c1_score ?? null,
    c2_score: values?.c2_score ?? null,
    d1_score: values?.d1_score ?? null,
    d2_score: values?.d2_score ?? null,
    hinhthuc1_score: values?.hinhthuc1_score ?? null,
    hinhthuc2_score: values?.hinhthuc2_score ?? null,
    total_score: values?.total_score ?? null,
    c_hieuqua: values?.c_hieuqua || '',
    c_nhanrong: values?.c_nhanrong || '',
    ca1: values?.ca1 || null,
    ca2: values?.ca2 || null,
    ca3: values?.ca3 || null,
    ca_du_dk: values?.ca_du_dk || null,
    cb1: values?.cb1 || null,
    cb2: values?.cb2 || null,
    cb3: values?.cb3 || null,
    cb_du_dk: values?.cb_du_dk || null
  };
};

export const getCertificateScore = (initiativeDetail: any, user: any): CertificateScore => {
  const values = (() => {
    if (initiativeDetail?.teacher_score3 === user?.id) {
      return initiativeDetail?.status_cham_diem === 1
        ? initiativeDetail?.certificate_cv1
        : initiativeDetail?.certificate_cv1;
    }
    if (initiativeDetail?.teacher_score4 === user?.id) {
      return initiativeDetail?.status_cham_diem2 === 1
        ? initiativeDetail?.certificate_cv2
        : initiativeDetail?.certificate_cv2;
    }
    return null;
  })();

  return prepareCertificateScore(values);
};

export const convertCertificateScoreForRequest = (score: SubmitScoreData): CertificateScore => {
  return {
    a1_score: score.a1_score ?? null,
    a2_score: score.a2_score ?? null,
    b1_score: score.b1_score ?? null,
    b2_score: score.b2_score ?? null,
    b3_score: score.b3_score ?? null,
    b4_score: score.b4_score ?? null,
    b5_score: score.b5_score ?? null,
    c1_score: score.c1_score ?? null,
    c2_score: score.c2_score ?? null,
    d1_score: score.d1_score ?? null,
    d2_score: score.d2_score ?? null,
    hinhthuc1_score: score.hinhthuc1_score ?? null,
    hinhthuc2_score: score.hinhthuc2_score ?? null,
    total_score: score.summary_score ?? null,
    c_hieuqua: score.c_hieuqua || '',
    c_nhanrong: score.c_nhanrong || '',
    ca1: revertYesNoResult(score.ca1),
    ca2: revertYesNoResult(score.ca2),
    ca3: revertYesNoResult(score.ca3),
    ca_du_dk: revertYesNoResult(score.ca_du_dk),
    cb1: revertYesNoResult(score.cb1),
    cb2: revertYesNoResult(score.cb2),
    cb3: revertYesNoResult(score.cb3),
    cb_du_dk: revertYesNoResult(score.cb_du_dk)
  };
};

export const initiativeComment = {
  a1: 'Trên cơ sở kinh nghiệm trong công tác quản lý, giảng dạy, giáo dục…phát hiện và xây dựng được nội dung, phương pháp mới.',
  a2: 'Có tính đột phá, phù hợp và nâng cao được hiệu quả, chất lượng trong quá trình thực hiện công tác của mình.',
  b1: 'Có luận đề: Đặt vấn đề gọn, rõ ràng (giới thiệu được khái quát thực trạng, mục đích ý nghĩa cần đạt, những giới hạn cần có…).',
  b2: 'Có luận điểm: Những biện pháp tổ chức thực hiện cụ thể.',
  b3: 'Có luận cứ khoa học, xác thực: thông qua các phương pháp pháp hoạt động thực tế.',
  b4: 'Có luận chứng: những minh chứng cụ thể (số liệu, hình ảnh,…) để thuyết phục được người đọc.',
  b5: 'Toàn bộ nội dung được trình bày hợp lý; có quan hệ chặt chẽ giữa các vấn đề được nêu; có sử dụng các phương pháp để phân tích, so sánh, tổng hợp, khái quát được mục tiêu, vấn đề nêu ra; phù hợp với qui luật và xu thế chung, không phải là ngẫu nhiên.',
  c1: 'Mang tính khả thi; khả năng ứng dụng đại trà.',
  c2: 'Được các CB-GV-NV trong đơn vị, ngành vận dụng vào công việc của mình đạt kết quả cao.',
  d1: 'Đem lại hiệu quả rõ rệt trong công tác quản lý, giảng dạy và giáo dục; trong việc tiếp nhận tri thức khoa học hay hình thành kỹ năng thực hành của học sinh. ',
  d2: 'Áp dụng trong thực tế đạt được hiệu quả cao nhất với lượng thời gian và sức lực được sử dụng ít nhất, tiết kiệm nhất; mang lại lợi ích kinh tế (mức độ làm lợi bằng tiền – nếu tính được) hoặc lợi ích xã hội.',
  hinhthuc1:
    'Trình bày nội dung theo bố cục đã nêu trên; từ ngữ và ngữ pháp được sử dụng chính xác, khoa học; các kiến thức được hệ thống hóa một cách chặt chẽ phù hợp với đổi mới giáo dục hiện nay.',
  hinhthuc2:
    'Đề tài được soạn thảo và in trên máy vi tính, trang trí khoa học, đóng bìa đẹp. Bìa SKKN phải được ghi rõ ràng theo trật tự sau: tên cơ quan chủ quản, tên đơn vị; tên đề tài; tên tác giả; chức danh; năm thực hiện (theo mẫu).'
};
